/**
 * OrderTypeSelector — Delivery / Collection picker embedded in chat messages.
 */

import {
  DirectionsRun as CollectionIcon,
  LocalShipping as DeliveryIcon,
} from '@mui/icons-material';
import { Box, Card, Typography } from '@mui/material';

interface OrderTypeSelectorProps {
  language: 'en' | 'ar';
  onSelect: (type: 'delivery' | 'collection') => void;
  disabled?: boolean;
}

export default function OrderTypeSelector({ language, onSelect, disabled }: OrderTypeSelectorProps) {
  const isAr = language === 'ar';

  const options = [
    {
      type: 'delivery' as const,
      icon: <DeliveryIcon sx={{ fontSize: 28, color: 'primary.main' }} />,
      label: isAr ? 'توصيل' : 'Delivery',
      desc: isAr ? 'نوصلها لبابك' : 'Delivered to your door',
    },
    {
      type: 'collection' as const,
      icon: <CollectionIcon sx={{ fontSize: 28, color: 'secondary.main' }} />,
      label: isAr ? 'استلام' : 'Collection',
      desc: isAr ? 'استلمها بنفسك' : 'Pick up from us',
    },
  ];

  return (
    <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
      {options.map(({ type, icon, label, desc }) => (
        <Card
          key={type}
          elevation={0}
          onClick={() => !disabled && onSelect(type)}
          sx={{
            flex: 1,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
            cursor: disabled ? 'default' : 'pointer',
            transition: 'all 0.15s',
            '&:hover': disabled
              ? {}
              : {
                  borderColor: type === 'delivery' ? 'primary.main' : 'secondary.main',
                  bgcolor: type === 'delivery' ? 'primary.50' : 'secondary.50',
                  transform: 'translateY(-2px)',
                },
          }}
        >
          {icon}
          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
            {label}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', textAlign: 'center' }}>
            {desc}
          </Typography>
        </Card>
      ))}
    </Box>
  );
}
